import type { CSSProperties, FC } from 'react';
import type { Agent } from '../agent';
import { cardBranding } from '../branding';
import { useOnVisible } from '../hooks/useOnVisible';
import { CARD_SHELL_CLASSNAME } from '../pristips/dimensions';
import { agentPhotoSrcSet, resizeImage } from '../utils/resizeImage';
import { trackClickAgent, trackViewAgent } from '../utils/tracking';
import { CardAction } from './CardAction';

interface Props {
    agent: Agent;
    position: number;
}

/** Cards past the first page start off-screen: two images each, worth keeping off the first paint. */
const CARDS_IN_VIEW = 4;

export const MeglerpromoCard: FC<Props> = ({ agent, position }) => {
    const branding = cardBranding(agent);
    const cardRef = useOnVisible<HTMLAnchorElement>(() => trackViewAgent(agent, position), {
        requireFullyVisible: true,
    });
    const loading = position < CARDS_IN_VIEW ? 'eager' : 'lazy';

    return (
        <a
            ref={cardRef}
            className={`${CARD_SHELL_CLASSNAME} flex flex-col items-center pt-12 px-16`}
            style={{ background: branding.background, color: branding.text }}
            href={agent.profileUrl}
            target="_top"
            onClick={() => trackClickAgent(agent, position)}
            onAuxClick={(e) => {
                if (e.button === 1) trackClickAgent(agent, position);
            }}
        >
            <div
                className="flex items-center justify-center rounded-4 px-8 py-4 max-w-full"
                style={{ background: branding.logoBackground }}
            >
                {agent.orgLogo ? (
                    <img
                        src={resizeImage(agent.orgLogo, '240w')}
                        alt={agent.orgName}
                        className="h-[18px] max-w-[102px] object-contain"
                        loading={loading}
                        decoding="async"
                    />
                ) : (
                    <span className="text-xs font-bold h-[18px] flex items-center truncate">{agent.orgName}</span>
                )}
            </div>

            <div className="mt-12 rounded-full overflow-clip w-[80px] h-[80px] shrink-0">
                {agent.image ? (
                    <img
                        src={resizeImage(agent.image, '142w')}
                        srcSet={agentPhotoSrcSet(agent.image)}
                        alt={agent.name}
                        className="w-full h-full object-cover object-top"
                        width={80}
                        height={80}
                        loading={loading}
                        decoding="async"
                    />
                ) : (
                    <div className="w-full h-full s-bg-subtle" aria-hidden="true" />
                )}
            </div>

            <p className="mt-8 w-full text-s font-bold text-center line-clamp-2">{agent.name}</p>

            <CardAction
                className="meglerpromo-cta"
                style={
                    {
                        '--mp-cta-bg': branding.button.background,
                        '--mp-cta-text': branding.button.text,
                        '--mp-cta-border': branding.button.border,
                        '--mp-cta-bg-hover': branding.button.backgroundHover,
                        '--mp-cta-text-hover': branding.button.textHover,
                        '--mp-cta-bg-active': branding.button.backgroundActive,
                    } as CSSProperties
                }
            >
                Vis profil
            </CardAction>
        </a>
    );
};
